import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

/**
 * The AG Grid theme shared by every records grid — the CPI operational view and the Asset
 * Inventory tabs. Header, filter menus, row pills and the light-theme overrides all live here so
 * the two grids cannot drift apart; each consumer supplies its own height and padding.
 */
export const RecordsGridContainer = styled(Box)`

  /* Light theme: drop AG grid's default enclosing wrapper frame. The rounded-pill rows
     (12px on first/last cell) and 4px row gap come from the shared row rules below; only
     the outer table border is unwanted. */
  html[data-theme='light'] & {
    .ag-root-wrapper,
    .ag-root {
      border: none !important;
    }
  }

  .ag-pinned-right-header,
  .ag-pinned-right-cols-container {
    border-left: none !important;
  }

  .ag-pinned-right-cols-container .ag-row {
    border-left: 0 !important;
    border-radius: 0 !important;
  }

  .ag-pinned-right-cols-container .ag-cell {
    border: none !important;
    background: transparent !important;
  }

  .ag-cell.ag-cell-last-left-pinned {
    border-right: none !important;
  }

  .ag-body-viewport .ag-center-cols-container .ag-row.main-row-with-action-link {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-right: 0 !important;
  }

  .ag-body-viewport .ag-center-cols-container .ag-row.has-action-link,
  .ag-body-viewport .ag-pinned-right-cols-container .ag-row.has-action-link {
    border-top-right-radius: 16px !important;
    border-bottom-right-radius: 16px !important;
  }

  .ag-resizer {
    &.ag-resizer-right {
      resize: none !important;
      display: none !important;
      pointer-events: none !important;
    }
  }

  /* Disable AG Grid's default highlight tint on selected rows — checkbox state is enough */
  --ag-selected-row-background-color: transparent;

  .ag-row.ag-row-selected,
  .ag-row.ag-row-selected.ag-row-even,
  .ag-row.ag-row-selected.ag-row-odd {
    background-color: inherit !important;

    &::before {
      background-color: transparent !important;
    }
  }

  /* Row selection + header select-all checkboxes (shared across Devices + Exclusion Rules tables) */
  /* SVGs are served from /public so CSS minification doesn't mangle inline data URIs. */
  .ag-header-select-all .ag-checkbox-input-wrapper,
  .ag-selection-checkbox .ag-checkbox-input-wrapper {
    width: 18px !important;
    height: 18px !important;
    background-color: transparent !important;
    background-image: url('/checkbox-unchecked.svg') !important;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border: none !important;
    box-shadow: none !important;

    &::after {
      content: none !important;
      display: none !important;
    }

    &::before {
      content: none !important;
      display: none !important;
    }

    &.ag-checked {
      background-image: url('/checkbox-checked.svg') !important;
    }

    &.ag-indeterminate {
      background-image: url('/checkbox-indeterminate.svg') !important;
    }
  }

  html[data-theme='light'] & {
    .ag-header-select-all .ag-checkbox-input-wrapper,
    .ag-selection-checkbox .ag-checkbox-input-wrapper {
      background-image: url('/checkbox-unchecked-light.svg') !important;

      &.ag-checked {
        background-image: url('/checkbox-checked-light.svg') !important;
      }

      &.ag-indeterminate {
        background-image: url('/checkbox-indeterminate-light.svg') !important;
      }
    }
  }

  .ag-popup {
    border: 0;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: ${theme.colors.devicesPopupBg};

    .ag-menu.ag-filter-menu {
      border: 0;
      border-radius: ${theme.attributes.borderRadius.tertiary};
      background: ${theme.colors.devicesPopupBg};

      .ag-filter {
        border: 0;
        border-radius: ${theme.attributes.borderRadius.tertiary};
        box-shadow: 0 4px 8px 0 ${theme.colors.primaryDark};
        width: 244px;

        .ag-filter-loading.ag-loading {
          background: ${theme.colors.devicesPopupBg};

          span {
            color: ${theme.colors.foreground};
          }
        }

        .ag-filter-wrapper {
          border: 0;
          border-radius: ${theme.attributes.borderRadius.tertiary};
          background: ${theme.colors.devicesPopupBg};
          height: auto;

          .ag-filter-body-wrapper {
            border: 0;
            border-radius: ${theme.attributes.borderRadius.tertiary};
            background: ${theme.colors.devicesPopupBg};

            .ag-picker-field.ag-labeled.ag-label-align-left.ag-select.ag-filter-select {
              .ag-wrapper.ag-picker-field-wrapper {
                background: ${theme.colors.devicesPopupBg};
                color: ${theme.colors.foreground};
                border: 1px solid ${theme.colors.devicesSelectBorder};
              }
            }

            .ag-mini-filter.ag-labeled {
              margin: ${theme.spacing.xSmall}px;

              .ag-wrapper.ag-input-wrapper.ag-text-field-input-wrapper {
                &::before {
                  background: ${theme.colors.devicesSecondaryText};
                  opacity: 1;
                  mask-image: url('/search-mask-image.svg');
                  width: 20px;
                  height: 20px;
                  margin-left: ${theme.spacing.small}px;
                }

                input {
                  background: ${theme.colors.devicesInputBg};
                  border: 1px solid ${theme.colors.devicesInputBg};
                  border-radius: ${theme.attributes.borderRadius.mediumSmall};
                  padding-left: 48px;
                  color: ${theme.colors.devicesSecondaryText};

                  font-size: ${theme.typography.fontSize.large};
                  font-style: normal;
                  font-weight: ${theme.typography.fontWeight.normal};
                  line-height: 20px;

                  &:focus {
                    box-shadow: none;
                  }

                  &:focus-within {
                    border-color: ${theme.colors.cta};
                  }

                  &::placeholder {
                    color: ${theme.colors.devicesSecondaryText};
                    font-size: ${theme.typography.fontSize.large};
                    font-style: normal;
                    font-weight: ${theme.typography.fontWeight.normal};
                    line-height: 20px;
                  }
                }
              }
            }

            .ag-set-filter-list {
              margin-right: 4px;
              height: auto;

              .ag-virtual-list-viewport {
                height: auto;
                max-height: 144px;

                ::-webkit-scrollbar {
                  width: 4px;
                  background: transparent;
                }

                ::-webkit-scrollbar-track {
                  box-shadow: none;
                  background: transparent;
                }

                ::-webkit-scrollbar-thumb {
                  background-color: ${theme.colors.surfaceContainerBackground};
                  border-radius: 2px;
                }
              }

              .ag-virtual-list-container.ag-filter-virtual-list-container {
                .ag-virtual-list-item.ag-filter-virtual-list-item {
                  cursor: pointer;

                  .ag-set-filter-item {
                    padding-left: ${theme.spacing.small}px !important;

                    .ag-set-filter-item-checkbox {
                      .ag-input-field-label {
                        color: ${theme.colors.devicesSecondaryText};
                        font-size: ${theme.typography.fontSize.large};
                        font-family: ${theme.typography.fontFamily.primary};
                        font-style: normal;
                        font-weight: ${theme.typography.fontWeight.bold};
                        line-height: 22px;
                      }

                      .ag-wrapper.ag-input-wrapper.ag-checkbox-input-wrapper {
                        background: transparent;
                        border: 0.5px solid ${theme.colors.checkboxUnchecked};
                      }
                    }
                  }

                  .ag-checkbox-input-wrapper {
                    &:not(.ag-checked) {
                      border: 0.5px solid ${theme.colors.devicesSecondaryText} !important;
                    }

                    &:where(.ag-indeterminate) {
                      &:after {
                        mask-image: url('/tree-indeterminate.svg') !important;
                      }
                    }
                  }

                  &[aria-checked='true'] {
                    .ag-set-filter-item {
                      .ag-set-filter-item-checkbox {
                        .ag-input-field-label {
                          color: ${theme.colors.menuItemText};
                        }

                        .ag-wrapper.ag-input-wrapper.ag-checkbox-input-wrapper {
                          border-color: ${theme.colors.menuItemText};

                          &:where(.ag-checked):after {
                            mask-image: url('/tick-3.svg');

                            html[data-theme='light'] & {
                              background-color: ${theme.colors.menuItemText};
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }

            .ag-picker-field.ag-labeled.ag-select.ag-filter-select {
              //display: none;
            }

            .ag-filter-body {
              margin-bottom: 0;

              .ag-filter-from,
              .ag-filter-to {
                &.ag-filter-date-from,
                &.ag-filter-date-to {
                  .ag-react-container {
                    .react-datepicker-wrapper {
                      width: 100%;

                      .react-datepicker__input-container {
                        input {
                          height: 48px;
                          background: transparent;
                          padding: 14px ${theme.spacing.small}px;
                          width: 100%;
                          border-radius: ${theme.attributes.borderRadius.tertiary};
                          border: 1px solid ${theme.colors.dropdownBorder};
                          color: ${theme.colors.menuItemText};
                          font-size: ${theme.typography.fontSize.large};
                          font-style: normal;
                          font-weight: ${theme.typography.fontWeight.bold};
                          line-height: 20px;

                          &:focus-visible,
                          &:focus,
                          &:active,
                          &:focus-within {
                            border-color: ${theme.colors.cta} !important;
                            outline: 0 !important;
                          }
                        }
                      }

                      .react-datepicker__calendar-icon {
                        right: ${theme.spacing.xSmall}px;
                        top: ${theme.spacing.xSmall}px;
                      }
                    }
                  }
                }
              }

              .ag-wrapper.ag-input-wrapper input {
                border-radius: ${theme.attributes.borderRadius.secondary};
                border: 1px solid ${theme.colors.devicesInputBg};
                background: ${theme.colors.devicesInputBg};
                color: ${theme.colors.devicesSecondaryText};
                font-size: ${theme.typography.fontSize.large};
                font-style: normal;
                font-weight: ${theme.typography.fontWeight.normal};
                line-height: 20px;

                &::placeholder {
                  color: ${theme.colors.devicesSecondaryText};
                  font-size: ${theme.typography.fontSize.large};
                  font-style: normal;
                  font-weight: ${theme.typography.fontWeight.normal};
                  line-height: 20px;
                }
              }
            }
          }

          .ag-filter-apply-panel {
            width: 100%;
            justify-content: flex-start;

            button {
              margin-left: 0;
              padding: ${theme.spacing.xSmall}px ${theme.spacing.small}px;
              background: transparent !important;
              outline: 0;
              border: 0;
              color: ${theme.colors.cta};
              font-family: ${theme.typography.fontFamily.primary};
              font-size: ${theme.typography.fontSize.large};
              font-style: normal;
              font-weight: ${theme.typography.fontWeight.extraThick};
              line-height: 22px;
              width: fit-content !important;
              height: fit-content !important;

              &:hover {
                color: ${theme.colors.buttonHollowHover};
              }
            }
          }

          /* Hide filter elements by default until values are loaded */
          .ag-mini-filter,
          .ag-virtual-list-viewport,
          .ag-filter-apply-panel {
            visibility: hidden;
          }

          /* Show elements when filter has values (class added by JS) */
          &.filter-has-values {
            .ag-mini-filter,
            .ag-virtual-list-viewport,
            .ag-filter-apply-panel {
              visibility: visible;
            }
          }

          /* Show no-data message styling */
          &.filter-empty {
            .ag-mini-filter,
            .ag-virtual-list-viewport,
            .ag-filter-apply-panel {
              display: none;
            }
          }
        }
      }
    }
  }

  .ag-root-wrapper {
    background: transparent;

    /* Date Picker Dropdown */
    .ag-popup {
      > * {
        background: ${theme.colors.devicesPopupBg};
        color: ${theme.colors.foreground};
      }
    }

    .ag-root {
      border-radius: ${theme.attributes.borderRadius.tertiary};
      background: transparent;
      gap: ${theme.spacing.half}px;
      margin-right: 12px;
      overflow: visible;

      .ag-body-horizontal-scroll-viewport {
        overflow-x: auto;
      }

      .ag-header {
        border-radius: ${theme.attributes.borderRadius.tertiary};
        background: ${theme.colors.devicesHeaderBg};
        height: 50px;

        html[data-theme='light'] & {
          border: 1px solid ${theme.colors.strokePrimary};
        }

        .ag-header-cell-label {
          flex: unset;

          .ag-sort-indicator-icon {
            .ag-icon {
              color: ${theme.colors.textSecondary};
            }
          }
        }

        .ag-header-row {
          .ag-header-cell {
            .ag-header-cell-resize {
              background: transparent;

              &:after {
                background: transparent;
              }
            }

            .ag-header-cell-comp-wrapper {
              .ag-cell-label-container {
                justify-content: flex-end;
                gap: 8px;

                .ag-header-icon.ag-header-cell-filter-button {
                  .ag-icon.ag-icon-filter {
                    &::before {
                      mask-image: url('/devices-filter-icon.svg');
                    }
                  }

                  /* AG Grid adds a hover background by default; we only want the icon to
                     tint to textPrimary on hover, so suppress the background. Skip the
                     active state — it has its own charcoalPlum background to preserve. */
                  &:hover:not(.ag-filter-active) {
                    cursor: pointer;
                    background: transparent !important;
                    outline-color: transparent !important;

                    .ag-icon.ag-icon-filter {
                      color: ${theme.colors.cta};
                    }
                  }

                  &.ag-has-popup-positioned-under {
                    .ag-icon.ag-icon-filter {
                      color: ${theme.colors.cta};
                    }
                  }

                  &.ag-filter-active {
                    background: ${theme.colors.dropdownBorder};
                    outline-color: ${theme.colors.dropdownBorder};
                    border-radius: ${theme.attributes.borderRadius.small};

                    html[data-theme='light'] & {
                      background: ${theme.colors.surfaceContainerBackground};
                      outline-color: ${theme.colors.surfaceContainerBackground};
                    }

                    &:after {
                      background-color: ${theme.colors.cta};
                      width: 5px;
                      height: 5px;
                      top: 0;
                      right: 0;
                    }

                    /* AG Grid applies --ag-active-color (blue) to icons on filter-active by
                       default; pin to dustyRoseGray to match the Figma spec. */
                    .ag-icon.ag-icon-filter {
                      color: ${theme.colors.devicesSecondaryText};
                      background: transparent;
                      outline: none;
                    }
                  }
                }
              }
            }

            .ag-header-cell-text {
              color: ${theme.colors.devicesSecondaryText};
              overflow: hidden;
              text-overflow: ellipsis;
              font-family: ${theme.typography.fontFamily.primary};
              font-size: ${theme.typography.fontSize.large};
              font-style: normal;
              font-weight: ${theme.typography.fontWeight.normal};
              line-height: 22px;
            }
          }

          .ag-header-icon {
            color: ${theme.colors.textSecondary};
          }
        }
      }

      .ag-body {
        .ag-body-vertical-scroll {
          position: absolute;
          right: -12px;
          top: -53px; /* Move up to start from header top */
          height: calc(100% + 53px); /* Extend height to include header area */
          width: 4px !important;
          min-width: 4px !important;
          max-width: 4px !important;
          overflow: hidden;

          > div {
            width: 4px !important;
            min-width: 4px !important;
            max-width: 4px !important;
          }

          .ag-body-vertical-scroll-viewport {
            width: 4px !important;
            min-width: 4px !important;
            max-width: 4px !important;
            overflow-y: scroll !important;
            overflow-x: hidden !important;

            &::-webkit-scrollbar {
              width: 4px !important;
              background: transparent;
            }

            &::-webkit-scrollbar-track {
              box-shadow: none;
              background: transparent;
            }

            &::-webkit-scrollbar-thumb {
              background: ${theme.colors.borderMedium};
              border-radius: ${theme.attributes.borderRadius.quaternary};
            }
          }

          .ag-body-vertical-scroll-container {
            width: 4px !important;
            min-width: 4px !important;
            max-width: 4px !important;
          }
        }

        .ag-body-viewport {
          .ag-full-width-container {
            .ag-row {
              background: ${theme.colors.devicesRowBorder};
              border-radius: ${theme.attributes.borderRadius.tertiary};

              .ag-loading {
                color: ${theme.colors.foreground};
              }
            }
          }

          .ag-viewport {
            .ag-center-cols-container {
              .ag-row-even,
              .ag-row-odd {
                /* ONYXIA-6836 */
                border-right: none !important;
                border-top-right-radius: 0 !important;
                border-bottom-right-radius: 0 !important;
              }
            }
          }

          .ag-center-cols-container,
          .ag-pinned-left-cols-container,
          .ag-pinned-right-cols-container {
            .ag-row {
              border-radius: ${theme.attributes.borderRadius.tertiary};
              outline: 0;
              overflow: hidden;

              &.ag-row-even {
                background: transparent;
                border: 1px solid ${theme.colors.devicesRowBorder};

                &.ag-row-hover {
                  background: ${theme.colors.devicesRowHoverBg} !important;

                  &:before {
                    background: ${theme.colors.devicesRowHoverBg} !important;
                  }
                }
              }

              &.ag-row-odd {
                background: ${theme.colors.devicesRowOddBg};
                border: 1px solid ${theme.colors.devicesRowBorder};

                html[data-theme='light'] & {
                  background: ${theme.colors.surfaceContainerBackground};
                }

                &.ag-row-hover {
                  background: ${theme.colors.devicesRowHoverBg} !important;

                  &:before {
                    background: ${theme.colors.devicesRowHoverBg} !important;
                  }
                }
              }

              .ag-cell {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                overflow: hidden;
                text-overflow: ellipsis;
                font-style: normal;
                color: ${theme.colors.foreground};
                font-family: ${theme.typography.fontFamily.primary};
                font-size: ${theme.typography.fontSize.large};
                font-weight: ${theme.typography.fontWeight.bold};

                &:focus-within {
                  border-color: transparent;
                }

                .MuiTypography-root {
                  overflow: hidden;
                  text-overflow: ellipsis;
                  font-style: normal;
                  color: ${theme.colors.foreground};
                  font-family: ${theme.typography.fontFamily.primary};
                  font-size: ${theme.typography.fontSize.large};
                  font-weight: ${theme.typography.fontWeight.bold};
                }

                &[col-id='HOSTNAME'] {
                  font-weight: 700;
                }
              }
            }
          }

          .ag-pinned-left-cols-container,
          .ag-pinned-right-cols-container {
            .ag-row {
              &.ag-row-even,
              &.ag-row-odd {
                /* ONYXIA-6836 */
                border-top-right-radius: 12px !important;
                border-bottom-right-radius: 12px !important;
              }
            }
          }
        }
      }
    }

    .ag-overlay {
      @media (max-height: 800px) {
        img {
          width: 64px;
        }

        .description-wrapper {
          .title {
            font-size: 18px;
          }

          .description {
            font-size: 12px;
          }
        }
      }

      @media (max-height: 600px) {
        img {
          display: none;
        }

        .description-wrapper {
          .title {
            font-size: 18px;
          }

          .description {
            display: none;
          }
        }
      }
    }
  }
`
